// <ca-meganav>: swaps the visible mega-menu panel from the L2 sidebar; header-menu still owns open/close.

class CaMegaNav extends HTMLElement {
  static #pool;

  #opened = false;
  #fitPending = false;
  #fitTimer = null;

  connectedCallback() {
    this.host = this.closest('.menu-list__list-item');
    this.card = this.querySelector('.ca-meganav__inner');
    (this.host ?? this).addEventListener('pointerenter', this.#onOpen);
    (this.host ?? this).addEventListener('focusin', this.#onOpen);

    this.sidebar = this.querySelector('.ca-meganav__sidebar');
    this.items = Array.from(this.querySelectorAll('[data-l2]'));
    this.panels = Array.from(this.querySelectorAll('[data-l2-panel]'));
    this.features = Array.from(this.querySelectorAll('[data-l2-feature]'));
    this.editorials = Array.from(this.querySelectorAll('.ca-meganav__feature-col [data-nav-match]'));
    if (this.items.length === 0) return;

    const first = this.items.find((el) => el.classList.contains('is-active')) ?? this.items[0];
    this.#syncEditorial(first.dataset.l2);

    for (const el of this.items) el.tabIndex = el === first ? 0 : -1;

    this.addEventListener('pointerover', this.#onPointer);
    // No focusin -> #activate: plain Tab focus must not swap the panel (S1 bug).
    if (this.sidebar) this.sidebar.addEventListener('keydown', this.#onArrowKeys);
    (this.host ?? this).addEventListener('keydown', this.#onEscape);

    if (this.card && 'ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.#fitHeight());
      this.resizeObserver.observe(this.card);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('pointerover', this.#onPointer);
    this.sidebar?.removeEventListener('keydown', this.#onArrowKeys);
    (this.host ?? this).removeEventListener('keydown', this.#onEscape);
    (this.host ?? this).removeEventListener('pointerenter', this.#onOpen);
    (this.host ?? this).removeEventListener('focusin', this.#onOpen);
    this.resizeObserver?.disconnect();
    clearTimeout(this.#fitTimer);
  }

  #onOpen = () => {
    this.#opened = true;
    this.#claimPanelsPool();
    const active = this.items.find((el) => el.classList.contains('is-active')) ?? this.items[0];
    if (active) {
      this.#syncEditorial(active.dataset.l2);
    } else {
      this.#syncVideo();
      this.#syncSlider();
    }
    this.#fitHeight();
    clearTimeout(this.#fitTimer);
    this.#fitTimer = setTimeout(this.#fitHeight, 80);
  };

  // Panels render once in <header-menu>; moved (not cloned) into whichever item is open.
  #claimPanelsPool() {
    const col = this.querySelector('.ca-meganav__feature-col');
    if (!col) return;
    if (!CaMegaNav.#pool?.isConnected) CaMegaNav.#pool = document.querySelector('[data-meganav-panels-pool]');
    const pool = CaMegaNav.#pool;
    if (!pool || pool.parentElement === col) return;
    if (pool.contains(document.activeElement)) return;
    col.appendChild(pool);
    this.editorials = Array.from(col.querySelectorAll('[data-nav-match]'));
  }

  #syncVideo() {
    if (!this.#opened || !this.editorials) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    for (const panel of this.editorials) {
      const video = panel.querySelector('video');
      if (!video) continue;
      if (panel.classList.contains('is-active') && video.dataset.poster && !video.getAttribute('poster')) {
        video.setAttribute('poster', video.dataset.poster);
      }
      if (panel.classList.contains('is-active') && !reduce) {
        const playing = video.play();
        if (playing && playing.catch) playing.catch(() => {});
      } else {
        video.pause();
      }
    }
  }

  // Sliders cannot self-start after a teleport — the opener drives them.
  #syncSlider() {
    if (!this.#opened || !this.editorials) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    for (const panel of this.editorials) {
      const slider = panel.querySelector('ca-slider');
      if (!slider) continue;
      if (panel.classList.contains('is-active') && !reduce) {
        slider.play?.();
      } else {
        slider.pause?.();
      }
    }
  }

  #onEscape = (event) => {
    if (event.key !== 'Escape') return;
    const trigger = (this.host ?? this).querySelector(':scope > .menu-list__link');
    if (!trigger) return;
    event.stopPropagation();
    trigger.setAttribute('aria-expanded', 'false');
    trigger.focus();
    // header-menu re-opens on focus; re-assert across two frames so the close wins the race.
    requestAnimationFrame(() => requestAnimationFrame(() => trigger.setAttribute('aria-expanded', 'false')));
  };

  #onPointer = (event) => {
    const item = event.target.closest('[data-l2]');
    if (item && this.contains(item)) this.#activate(item);
  };

  #onArrowKeys = (event) => {
    const current = event.target.closest('[data-l2]');
    if (!current || !this.sidebar.contains(current)) return;

    const i = this.items.indexOf(current);
    if (i === -1) return;
    const last = this.items.length - 1;
    let next = i;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        next = i === last ? 0 : i + 1;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        next = i === 0 ? last : i - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }
    event.preventDefault();
    const target = this.items[next];
    if (!target || target === current) return;
    this.#activate(target);
    target.focus();
  };

  #activate(item) {
    if (item.classList.contains('is-active')) return;
    this.#claimPanelsPool();
    const handle = item.dataset.l2;

    for (const el of this.items) {
      const on = el === item;
      el.classList.toggle('is-active', on);
      // No aria-current here: AT reads it as "current page" on a nav link.
      el.tabIndex = on ? 0 : -1;
    }

    for (const panel of this.panels) {
      panel.hidden = panel.dataset.l2Panel !== handle;
    }

    this.#syncEditorial(handle);
  }

  #syncEditorial(handle) {
    if (this.features) {
      for (const feature of this.features) {
        feature.hidden = feature.dataset.l2Feature !== handle;
      }
    }
    if (this.editorials) {
      for (const panel of this.editorials) {
        panel.classList.toggle('is-active', panel.dataset.navMatch === handle);
      }
    }
    this.#syncVideo();
    this.#syncSlider();
    this.#fitHeight();
  }

  // Write --submenu-height and --full-open-header-height together, or the card shifts and the menu closes.
  #fitHeight = () => {
    if (this.#fitPending) return;
    this.#fitPending = true;
    requestAnimationFrame(() => {
      this.#fitPending = false;
      // Gate: only touch the native height vars while this menu is open.
      const trigger = this.host?.querySelector(':scope > .menu-list__link');
      if (!trigger || trigger.getAttribute('aria-expanded') !== 'true') return;

      const header = this.closest('#header-component');
      const card = this.card ?? this.querySelector('.ca-meganav__inner');
      if (!header || !card) return;

      const styles = getComputedStyle(header);
      const fullOpen = parseFloat(styles.getPropertyValue('--full-open-header-height')) || 0;
      const submenuH = parseFloat(styles.getPropertyValue('--submenu-height')) || 0;
      if (fullOpen <= 0) return;
      const headerVisible = Math.max(0, fullOpen - submenuH);
      const cardStyles = getComputedStyle(card);
      const margins = (parseFloat(cardStyles.marginTop) || 0) + (parseFloat(cardStyles.marginBottom) || 0);
      const height = Math.ceil(card.getBoundingClientRect().height + margins);
      // Skip no-op writes: breaks the ResizeObserver feedback loop.
      if (Math.abs(height - submenuH) < 1) return;
      header.style.setProperty('--submenu-height', `${height}px`);
      header.style.setProperty('--full-open-header-height', `${height + headerVisible}px`);
    });
  };
}

if (!customElements.get('ca-meganav')) {
  customElements.define('ca-meganav', CaMegaNav);
}
